import { useState } from "preact/hooks";
import type { JSX } from "preact";
import type { ConversationDetail } from "../../api";
import { CopyIcon } from "../../components/Icons";
import { ago, agoPhrase, dateTime, statusLabel, visitorLabel } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { deviceLabel, placeLabel } from "../../lib/place";

/**
 * Everything the chat system itself knows about the person in the open
 * conversation. Deliberately nothing from the shops it serves: licence and
 * order data stay with them.
 */

/** Only http(s) becomes a link; anything else is shown as text. */
function safeHref(url: string): string | null {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search || "/";
  } catch {
    return url;
  }
}

export function ContextPanel({
  detail,
  open,
  isAdmin,
  onClose,
  onOpenPast,
  onBan,
  onUnban,
  onPurge,
  onEditVisitor,
}: {
  detail: ConversationDetail;
  open: boolean;
  /** Owners and admins only: erasing a person is not an everyday operator action. */
  isAdmin: boolean;
  onClose: () => void;
  onOpenPast: (id: number) => void;
  onBan: () => void;
  onUnban: () => void;
  onPurge: () => void;
  onEditVisitor?: () => void;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.context;
  const { visitor, conversation, pages, history, site } = detail;
  const [copied, setCopied] = useState(false);
  const name = visitorLabel(
    { visitorName: visitor?.displayName ?? null, visitorEmail: visitor?.email ?? null, visitorId: conversation.visitorId },
    lang,
  );

  return (
    <>
      <div class={`scrim${open ? " open" : ""}`} onClick={onClose} />
      <aside class={`pane context${open ? " open" : ""}`} aria-label={t.label}>
        <section class="card">
          <div class="person">
            <span class="avatar visitor" style={{ width: "44px", height: "44px", fontSize: "18px" }} aria-hidden="true">
              {name.charAt(0).toLocaleUpperCase(lang)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div class="name">{name}</div>
              {visitor?.email ? (
                <div class="mail">
                  {visitor.email}
                  <button
                    class="btn ghost icon"
                    style={{ width: "24px", height: "24px", padding: "4px" }}
                    aria-label={t.copyEmail}
                    title={copied ? t.copied : t.copyEmail}
                    onClick={() => {
                      void navigator.clipboard?.writeText(visitor.email!).then(() => {
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 1500);
                      });
                    }}
                  >
                    <CopyIcon />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {visitor?.identityVerified ? (
              <span class="chip verified" title={t.verifiedTitle}>
                {t.verified}
              </span>
            ) : (
              <span class="chip" title={t.unverifiedTitle}>
                {t.unverified}
              </span>
            )}
            {visitor?.isBanned ? (
              <span class="chip" style={{ color: "var(--danger)" }}>
                {t.banned}
              </span>
            ) : null}
            {visitor?.isLive ? <span class="chip live">{t.liveNow}</span> : null}
          </div>
          {/* A member's name and address come signed from the site; only an
              anonymous visitor's can be corrected here. */}
          {visitor && !visitor.identityVerified && onEditVisitor ? (
            <button class="btn sm" style={{ marginTop: "10px" }} onClick={onEditVisitor}>
              {t.editVisitor}
            </button>
          ) : null}
          {visitor?.externalId ? (
            <dl class="kv" style={{ marginTop: "10px" }}>
              <dt>{t.siteAccount}</dt>
              <dd>{visitor.externalId}</dd>
            </dl>
          ) : null}
        </section>

        {conversation.ticketRef ? (
          <section class="card">
            <h3>{t.ticket}</h3>
            <p style={{ margin: 0 }}>
              <strong>#{conversation.ticketRef.id}</strong>{" "}
              <span class="hint">{t.ticketOpened(dateTime(conversation.ticketRef.createdAt, lang))}</span>
            </p>
            {conversation.ticketRef.url && safeHref(conversation.ticketRef.url) ? (
              <a href={safeHref(conversation.ticketRef.url)!} target="_blank" rel="noopener noreferrer">
                {t.ticketOpen}
              </a>
            ) : null}
          </section>
        ) : null}

        <section class="card">
          <h3>{t.now}</h3>
          <dl class="kv">
            <dt>{t.page}</dt>
            <dd>
              {pages[0] ? (
                safeHref(pages[0].url) ? (
                  <a href={safeHref(pages[0].url)!} target="_blank" rel="noopener noreferrer">
                    {pages[0].title || pathOf(pages[0].url)}
                  </a>
                ) : (
                  pages[0].title || pages[0].url
                )
              ) : conversation.sourceUrl ? (
                pathOf(conversation.sourceUrl)
              ) : (
                <span class="hint">{t.unknown}</span>
              )}
            </dd>
            <dt>{all.common.site}</dt>
            <dd>{site?.name ?? "—"}</dd>
            {conversation.referrer ? (
              <>
                <dt>{t.referrer}</dt>
                <dd>{conversation.referrer}</dd>
              </>
            ) : null}
            <dt>{t.conversation}</dt>
            <dd>{t.startedAt(dateTime(conversation.createdAt, lang), agoPhrase(conversation.createdAt, Date.now(), lang))}</dd>
          </dl>
        </section>

        <section class="card">
          <h3>{t.pages}</h3>
          {pages.length === 0 ? (
            <p class="hint" style={{ margin: 0 }}>
              {t.pagesEmpty}
            </p>
          ) : (
            <ol class="trail">
              {pages.map((page, index) => {
                const href = safeHref(page.url);
                return (
                  <li key={`${page.url}-${index}`}>
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" title={page.url}>
                        {page.title || pathOf(page.url)}
                      </a>
                    ) : (
                      <span>{page.title || page.url}</span>
                    )}
                    <span class="t">{ago(page.visitedAt, Date.now(), lang)}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        <section class="card">
          <h3>{t.device}</h3>
          <dl class="kv">
            <dt>{t.browser}</dt>
            <dd>{visitor?.browser ?? "—"}</dd>
            <dt>{t.os}</dt>
            <dd>{visitor?.os ?? "—"}</dd>
            <dt>{t.deviceType}</dt>
            <dd>{deviceLabel(visitor?.device, all.visitors.devices) ?? "—"}</dd>
            <dt>{t.country}</dt>
            <dd>{placeLabel(visitor?.country, lang) ?? "—"}</dd>
            <dt>{all.visitors.ip}</dt>
            <dd>{visitor?.ip ? <code class="ip">{visitor.ip}</code> : "—"}</dd>
            {visitor ? (
              <>
                <dt>{t.firstVisit}</dt>
                <dd>{dateTime(visitor.firstSeenAt, lang)}</dd>
                <dt>{t.lastSeen}</dt>
                <dd>{agoPhrase(visitor.lastSeenAt, Date.now(), lang)}</dd>
              </>
            ) : null}
          </dl>
        </section>

        <section class="card">
          <h3>{t.past}</h3>
          {history.length === 0 ? (
            <p class="hint" style={{ margin: 0 }}>
              {t.firstChat}
            </p>
          ) : (
            <ul class="past">
              {history.map((past) => (
                <li key={past.id}>
                  <button onClick={() => onOpenPast(past.id)}>
                    <span class="p">{past.preview || t.emptyPreview}</span>
                    <span class="m">
                      {dateTime(past.createdAt, lang)}, {statusLabel(past.status, lang).toLocaleLowerCase(lang)}{" "}
                      {past.rating ? (
                        <span class="stars" aria-label={t.stars(past.rating)}>
                          {"★".repeat(past.rating)}
                          <span style={{ opacity: 0.3 }}>{"★".repeat(5 - past.rating)}</span>
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {visitor ? (
          <section class="card">
            <h3>{t.actions}</h3>
            <div class="actions-row">
              {visitor.isBanned ? (
                <button class="btn sm" onClick={onUnban}>
                  {t.unban}
                </button>
              ) : (
                <button class="btn sm danger" onClick={onBan}>
                  {t.ban}
                </button>
              )}
            </div>
            {/* Erasure sits apart from the ban, with its own warning: a ban is
                reversible and this is not. */}
            {isAdmin ? (
              <>
                <button class="btn sm danger" style={{ marginTop: "10px" }} onClick={onPurge}>
                  {t.purge}
                </button>
                <p class="hint" style={{ marginTop: "6px" }}>
                  {t.purgeHint}
                </p>
              </>
            ) : null}
          </section>
        ) : null}
      </aside>
    </>
  );
}
